import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { authKeys } from './auth'
import { errorMessage } from './errors'
import { invoiceApi } from './invoice-api'
import type { CorrectionResult } from './invoice-api/adapter'
import type { Me } from './types'
import { todayIsoDate } from './week'
import type {
  BulkTransitionAction,
  BulkTransitionResult,
  CustomInvoiceInput,
  Invoice,
  InvoiceComment,
  InvoiceListParams,
  PayoutInput,
  SendInput,
  SetDistributionInput,
  UpdateDraftInput,
} from './invoice-types'

export const invoiceKeys = {
  list: (params: InvoiceListParams) => ['invoices', 'list', params] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
  templates: ['invoices', 'templates'] as const,
  clients: ['invoices', 'clients'] as const,
}

/**
 * De unieke redenen achter de mislukte facturen; zonder dit blijft een geblokkeerde
 * bulk-actie ("geen factuur-e-mailadres") onverklaard.
 */
function bulkFailureDetail(results: BulkTransitionResult[]): string {
  const reasons = [...new Set(results.filter((r) => !r.ok && r.error).map((r) => r.error as string))]
  if (reasons.length === 0) return ''
  const shown = reasons.slice(0, 2).join(' · ')
  return reasons.length > 2 ? `${shown} · en ${reasons.length - 2} andere` : shown
}

export function useInvoiceList(params: InvoiceListParams, enabled = true) {
  return useQuery({ queryKey: invoiceKeys.list(params), queryFn: () => invoiceApi.list(params), enabled })
}

export function useInvoice(id: string) {
  return useQuery({ queryKey: invoiceKeys.detail(id), queryFn: () => invoiceApi.get(id) })
}

export function useInvoiceTemplates() {
  return useQuery({ queryKey: invoiceKeys.templates, queryFn: () => invoiceApi.templates() })
}

export function useInvoiceClients() {
  return useQuery({ queryKey: invoiceKeys.clients, queryFn: () => invoiceApi.clients() })
}

export function useMyPayouts() {
  return useQuery({ queryKey: ['invoices', 'my-payouts'], queryFn: () => invoiceApi.myPayouts() })
}

export function useInvoiceAction(id: string) {
  const queryClient = useQueryClient()

  function settle(invoice: Invoice, message?: string) {
    queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice)
    queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
    if (message) toast.success(message)
  }

  const submit = useMutation({
    mutationFn: () => invoiceApi.submit(id),
    onSuccess: (inv) => settle(inv, 'Ingediend ter approval'),
    onError: (e) => toast.error(errorMessage(e, 'Indienen mislukt')),
  })
  const approve = useMutation({
    mutationFn: (issueDate?: string) => invoiceApi.approve(id, issueDate),
    onSuccess: (inv) => settle(inv, `Goedgekeurd${inv.fullNumber ? ` · ${inv.fullNumber}` : ''}`),
    onError: (e) => toast.error(errorMessage(e, 'Goedkeuren mislukt')),
  })
  const reject = useMutation({
    mutationFn: (comment: string) => invoiceApi.reject(id, comment),
    onSuccess: (inv) => settle(inv, 'Afgekeurd, terug naar concept'),
    onError: (e) => toast.error(errorMessage(e, 'Afkeuren mislukt')),
  })
  const send = useMutation({
    mutationFn: (input: SendInput) => invoiceApi.send(id, input),
    onSuccess: (inv) => settle(inv, 'Factuur verzonden'),
    onError: (e) => toast.error(errorMessage(e, 'Versturen mislukt')),
  })
  const markSent = useMutation({
    mutationFn: () => invoiceApi.markSent(id),
    onSuccess: (inv) => settle(inv, 'Gemarkeerd als verzonden'),
    onError: (e) => toast.error(errorMessage(e, 'Markeren mislukt')),
  })
  const registerPayment = useMutation({
    mutationFn: (input: { paidAt: string; note?: string }) => invoiceApi.registerPayment(id, input),
    onSuccess: (inv) => settle(inv, 'Betaling geregistreerd'),
    onError: (e) => toast.error(errorMessage(e, 'Registreren mislukt')),
  })
  const voidInvoice = useMutation({
    mutationFn: (reason: string) => invoiceApi.voidInvoice(id, reason),
    onSuccess: (inv) => settle(inv, 'Factuur gestorneerd'),
    onError: (e) => toast.error(errorMessage(e, 'Storneren mislukt')),
  })
  const sendReminder = useMutation({
    mutationFn: (input: SendInput) => invoiceApi.sendReminder(id, input),
    onSuccess: (inv) => settle(inv, 'Betalingsherinnering verstuurd'),
    onError: (e) => toast.error(errorMessage(e, 'Herinnering versturen mislukt')),
  })
  const unapprove = useMutation({
    mutationFn: (reason: string) => invoiceApi.unapprove(id, reason),
    onSuccess: (inv) => settle(inv, 'Goedkeuring ingetrokken'),
    onError: (e) => toast.error(errorMessage(e, 'Intrekken mislukt')),
  })
  const recalculate = useMutation({
    mutationFn: () => invoiceApi.recalculate(id),
    onSuccess: (inv) => settle(inv, 'Herberekend'),
    onError: (e) => toast.error(errorMessage(e, 'Herberekenen mislukt')),
  })
  const park = useMutation({
    mutationFn: (input: { releaseDate?: string | null; reason?: string | null }) => invoiceApi.park(id, input),
    onSuccess: (inv) => settle(inv, 'Factuur geparkeerd'),
    onError: (e) => toast.error(errorMessage(e, 'Parkeren mislukt')),
  })
  const release = useMutation({
    mutationFn: () => invoiceApi.release(id),
    onSuccess: (inv) => settle(inv, 'Vrijgegeven naar concept'),
    onError: (e) => toast.error(errorMessage(e, 'Vrijgeven mislukt')),
  })
  const escalate = useMutation({
    mutationFn: (reason: string) => invoiceApi.escalate(id, reason),
    onSuccess: (inv) => settle(inv, 'Factuur geëscaleerd'),
    onError: (e) => toast.error(errorMessage(e, 'Escaleren mislukt')),
  })
  const deEscalate = useMutation({
    mutationFn: () => invoiceApi.deEscalate(id),
    onSuccess: (inv) => settle(inv, 'Escalatie teruggedraaid'),
    onError: (e) => toast.error(errorMessage(e, 'Terugdraaien mislukt')),
  })
  const withdraw = useMutation({
    mutationFn: (input: { reason: string; createCredit: boolean }) => invoiceApi.withdraw(id, input),
    onSuccess: (result) => {
      settle(result.invoice, result.credit ? 'Ingetrokken, creditnota aangemaakt' : 'Factuur ingetrokken')
      if (result.credit) queryClient.setQueryData(invoiceKeys.detail(result.credit.id), result.credit)
    },
    onError: (e) => toast.error(errorMessage(e, 'Intrekken mislukt')),
  })
  const writeOff = useMutation({
    mutationFn: (reason: string) => invoiceApi.writeOff(id, reason),
    onSuccess: (inv) => settle(inv, 'Oninbaar geboekt'),
    onError: (e) => toast.error(errorMessage(e, 'Oninbaar boeken mislukt')),
  })
  const reopen = useMutation({
    mutationFn: () => invoiceApi.reopen(id),
    onSuccess: (inv) => settle(inv, 'Heropend naar verzonden'),
    onError: (e) => toast.error(errorMessage(e, 'Heropenen mislukt')),
  })

  return {
    submit,
    approve,
    reject,
    send,
    sendReminder,
    markSent,
    registerPayment,
    voidInvoice,
    unapprove,
    recalculate,
    park,
    release,
    escalate,
    deEscalate,
    withdraw,
    writeOff,
    reopen,
  }
}

export function useDeleteInvoice(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => invoiceApi.remove(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      toast.success('Concept verwijderd, de uren staan weer open')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verwijderen mislukt')),
  })
}

export function useBulkTransition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { ids: string[]; action: BulkTransitionAction; issueDate?: string | null }) => invoiceApi.bulkTransition(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      const verb = result.action === 'delete' ? 'verwijderd' : 'bijgewerkt'
      if (result.failedCount === 0) {
        toast.success(`${result.okCount} facturen ${verb}`)
      } else {
        toast.warning(`${result.okCount} gelukt, ${result.failedCount} mislukt`, {
          description: bulkFailureDetail(result.results) || undefined,
        })
      }
    },
    onError: (e) => toast.error(errorMessage(e, 'Bulk-actie mislukt')),
  })
}

export function useCorrectOrCredit(id: string) {
  const queryClient = useQueryClient()
  function onNew(inv: Invoice, message: string) {
    queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
    queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
    toast.success(message)
  }
  function onCorrection(result: CorrectionResult) {
    queryClient.setQueryData(invoiceKeys.detail(result.credit.id), result.credit)
    queryClient.setQueryData(invoiceKeys.detail(result.draft.id), result.draft)
    queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
    queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) })
    toast.success('Creditnota-concept en correctie-concept aangemaakt')
  }
  const correct = useMutation({
    mutationFn: () => invoiceApi.correct(id),
    onError: (e) => toast.error(errorMessage(e, 'Corrigeren mislukt')),
  })
  const credit = useMutation({
    mutationFn: () => invoiceApi.credit(id),
    onError: (e) => toast.error(errorMessage(e, 'Crediteren mislukt')),
  })
  return { correct, credit, onNew, onCorrection }
}

export function useAddComment(id: string) {
  const queryClient = useQueryClient()
  const key = invoiceKeys.detail(id)
  return useMutation({
    mutationFn: (body: string) => invoiceApi.addComment(id, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Invoice>(key)
      if (previous) {
        const me = queryClient.getQueryData<Me | null>(authKeys.me)
        const optimistic: InvoiceComment = {
          id: `temp_${previous.comments.length}`,
          authorName: me?.user.displayName ?? '',
          body,
          createdAt: new Date().toISOString(),
        }
        queryClient.setQueryData<Invoice>(key, { ...previous, comments: [...previous.comments, optimistic] })
      }
      return { previous }
    },
    onError: (error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toast.error(errorMessage(error, 'Reactie plaatsen mislukt'))
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

export function useSetPayout(id: string) {
  const queryClient = useQueryClient()
  const key = invoiceKeys.detail(id)
  return useMutation({
    mutationFn: ({ lineId, input }: { lineId: string; input: PayoutInput }) => invoiceApi.setPayout(id, lineId, input),
    onMutate: async ({ lineId, input }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Invoice>(key)
      if (previous) {
        queryClient.setQueryData<Invoice>(key, {
          ...previous,
          distribution: previous.distribution.map((d) =>
            d.id === lineId
              ? {
                  ...d,
                  payoutStatus: input.payoutStatus,
                  payoutPaidAt: input.payoutPaidAt ?? (input.payoutStatus === 'PAID' ? todayIsoDate() : null),
                  incomingInvoiceNumber: input.incomingInvoiceNumber ?? d.incomingInvoiceNumber,
                }
              : d,
          ),
        })
      }
      return { previous }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toast.error(errorMessage(error, 'Uitbetaalstatus opslaan mislukt'))
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

export function useUpdateDraft(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateDraftInput) => invoiceApi.updateDraft(id, input),
    onSuccess: (inv) => {
      queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useSetDistribution(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetDistributionInput) => invoiceApi.setDistribution(id, input),
    onSuccess: (inv) => {
      queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      toast.success('Verdeling opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Verdeling opslaan mislukt')),
  })
}

export function useClearDistribution(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => invoiceApi.clearDistribution(id),
    onSuccess: (inv) => {
      queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      toast.success('Verdeling volgt weer het klant-script')
    },
    onError: (e) => toast.error(errorMessage(e, 'Terugzetten mislukt')),
  })
}

export function useSetNotes(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notes: string | null) => invoiceApi.setNotes(id, notes),
    onSuccess: (inv) => {
      queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      toast.success('Toelichting opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Toelichting opslaan mislukt')),
  })
}

export function usePreviewFromHours() {
  return useMutation({
    mutationFn: (input: { periodStart: string; periodEnd: string; projectIds?: string[] }) => invoiceApi.previewFromHours(input),
    onError: (e) => toast.error(errorMessage(e, 'Preview mislukt')),
  })
}

export function useGenerateFromHours() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { periodStart: string; periodEnd: string; clientIds: string[]; projectIds?: string[] }) => invoiceApi.generateFromHours(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
      toast.success(`${result.created.length} concept(en) aangemaakt`)
      for (const warning of result.warnings) {
        toast.warning(warning, { duration: 10000 })
      }
    },
    onError: (e) => toast.error(errorMessage(e, 'Genereren mislukt')),
  })
}

export function useCreateCustom() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CustomInvoiceInput) => invoiceApi.createCustom(input),
    onSuccess: (inv) => {
      queryClient.setQueryData(invoiceKeys.detail(inv.id), inv)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}
